import { Button, Center, Loader, Paper, ScrollArea, Stack, Text, ThemeIcon, useMantineTheme, Group, ActionIcon, Tooltip } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { IconBox, IconChevronRight, IconTrash } from "@tabler/icons-react";
import cx from "clsx";

import { ListAssetDto } from "../../models/assets.models";
import { useFilteredAssets } from "../../functions/asset-filter.hooks";
import { useSearch } from "../../functions/SearchBox.hook";
import { NoSearchResults } from "../Shared/NoSearchResults";
import classes from "./AssetList.module.css";

interface AssetListProps {
	assets?: ListAssetDto[] | undefined;
	isLoading?: boolean;
	selectedAsset?: ListAssetDto | null;
	setSelectedAsset?: (asset: ListAssetDto | null) => void;
	parentAssetFilter?: ListAssetDto | null;
	onClearParentFilter?: () => void;
	onSetParentFilter?: (asset: ListAssetDto) => void;
}

export const AssetList = ({ assets, isLoading, selectedAsset, setSelectedAsset, parentAssetFilter, onClearParentFilter, onSetParentFilter }: AssetListProps) => {
	const theme = useMantineTheme();
	const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.xs})`, true);
	const { query, setQuery } = useSearch();
	const { filteredAssets, showNoResults } = useFilteredAssets(assets || []);

	// Further filter by parent asset if specified
	const displayedAssets = parentAssetFilter
		? filteredAssets.filter(asset => asset.ParentAsset?.AssetId === parentAssetFilter.AssetId)
		: filteredAssets;

	if (isLoading) return (
		<Paper withBorder p={0} radius="md" style={{ overflow: "hidden" }}>
			<ScrollArea type={isMobile ? "never" : "scroll"} style={{ height: isMobile ? "unset" : "calc(100vh - 160px)", minHeight: isMobile ? "calc(100vh - 150px)" : "calc(100vh - 160px)" }}>
				<Center>
					<Loader size="sm" mt="lg" />
				</Center>
			</ScrollArea>
		</Paper>
	);

	const handleAssetSelect = (e: React.MouseEvent<HTMLAnchorElement>, asset: ListAssetDto): void => {
		e.preventDefault();
		setSelectedAsset?.(asset);
	};

	const handleSetParentFilter = (e: React.MouseEvent, asset: ListAssetDto): void => {
		e.preventDefault();
		e.stopPropagation();
		onSetParentFilter?.(asset);
		setQuery(""); // Clear search when drilling down to child assets
	}

	return (
		<Paper withBorder p={0} radius="md" style={{ overflow: "hidden" }}>
			<ScrollArea type={isMobile ? "never" : "hover"} style={{ height: isMobile ? "unset" : "calc(100vh - 160px)", minHeight: isMobile ? "calc(100vh - 150px)" : "calc(100vh - 160px)" }}>
				{parentAssetFilter && (
					<Stack gap="sm" px="md" py="md" style={{ borderBottom: "1px solid var(--mantine-color-default-border)" }}>
						<Text size="sm" fw="500">Viewing children of: <Text component="span" fw="bold">{parentAssetFilter.Name}</Text></Text>
						<Button variant="light" radius="lg" size="xs" onClick={onClearParentFilter}>
							Back to All Assets
						</Button>
					</Stack>
				)}
				{displayedAssets?.map(asset => (
					<Button
						key={asset.AssetId}
						component="a"
						href={`/assets/${asset.AssetId}`}
						onClick={(e) => handleAssetSelect(e, asset)}
						className={cx(classes["asset-list-item"], { [classes["selected-item"]]: selectedAsset?.AssetId === asset.AssetId })}
						w="100%"
						leftSection={
							<ThemeIcon radius="xl" color={!asset.Deleted ? "blue" : "red"} size="xl" style={{ border: "1px solid var(--mantine-color-default-border)" }} variant="light">
								{!asset.Deleted ? <IconBox size={24} /> : <IconTrash size={24} />}
							</ThemeIcon>
						}
						rightSection={
							<Tooltip label="View child assets" color="var(--mantine-primary-color-filled)" withArrow>
								<ActionIcon
									variant="subtle"
									// color="gray"
									radius="xl"
									onClick={(e) => handleSetParentFilter(e, asset)}
									size="xl"
									pl={5}
								>
									<IconChevronRight size={30} />
								</ActionIcon>
							</Tooltip>
						}
					>
						<Stack gap={2} align="flex-start">
							<Text fw={500} c={asset.Deleted ? "dimmed" : ""}>{asset.Name}</Text>
							{(asset.AssetType || asset.Location?.Title) && (
								<Text fz="xs" c="dimmed">
									{asset.AssetType || ""}{asset.AssetType && asset.Location?.Title ? " - " : ""}{asset.Location?.Title || ""}
								</Text>
							)}
						</Stack>
					</Button>
				))}
				{showNoResults && <NoSearchResults searchType="assets" query={query} setQuery={setQuery} />}
				{displayedAssets?.length === 0 && assets?.length !== 0 && !showNoResults && (
					<Stack align="center" justify="center" mt="xl" px="sm">
						<ThemeIcon size={100} radius={100} variant="light" color="gray">
							<IconBox size={60} />
						</ThemeIcon>
						<Text style={{ textAlign: "center" }} lineClamp={1} c="dimmed">No child assets found.</Text>
					</Stack>
				)}
				{assets?.length === 0 && (
					<Stack align="center" justify="center" mt="xl" px="sm">
						<ThemeIcon size={100} radius={100} variant="light" color="gray">
							<IconBox size={60} />
						</ThemeIcon>
						<Text style={{ textAlign: "center" }} lineClamp={1} c="dimmed">No assets have been created yet.</Text>
					</Stack>
				)}
			</ScrollArea>
		</Paper>
	);
};
